import type { RouteLocationNormalizedGeneric } from 'vue-router';

type NavigationRoute = Pick<RouteLocationNormalizedGeneric, 'path' | 'fullPath' | 'matched'>;

interface IsQueryOnlyNavigationOptions {
    /** Tests set this flag, because `import.meta.client` has no value in Vitest. */
    readonly isClient?: boolean;
    /** Tests set this flag, because `useNuxtApp()` needs the Nuxt context. The default is `useNuxtApp().isHydrating`. */
    readonly isHydrating?: boolean;
}

/**
 * Whether a navigation in the browser changes only the query or the hash of the current page.
 *
 * Route middleware uses this to skip its checks. A page that changes only its query is not a new
 * page: the middleware checked it when the user opened it. The server always returns `false`, so a
 * server render runs all checks.
 *
 * The function returns `false` when it is not sure, so that the checks run:
 * - The initial navigation of the browser does not run middleware. Nuxt runs the middleware for the
 *   first page when it hydrates the server render. This run uses the path that the server saw, and
 *   the browser can have a different query. Because of this, the function returns `false` while
 *   Nuxt hydrates.
 * - The first navigation has no previous page. `START_LOCATION` has no matched records.
 * - A navigation to the same URL is not a change of the query.
 * - The paths are compared exactly. A path that differs only in a trailing slash or in its case is a
 *   different path.
 */
export const isQueryOnlyNavigation = (
    to: NavigationRoute,
    from: NavigationRoute,
    { isClient = import.meta.client, isHydrating }: IsQueryOnlyNavigationOptions = {}
): boolean => {
    if (!isClient) return false;
    if (isHydrating ?? useNuxtApp().isHydrating) return false;
    if (from.matched.length === 0) return false;
    if (to.fullPath === from.fullPath) return false;

    return to.path === from.path;
};
